import { ReservationsDataOutDTO } from '@api/ringil-api.v2';
import { formatVolume, formatWeight, isNotBlank } from '@utils/utils';

/**
 * Value derivation shared by the card and the detail renderers, so an attribute reads the same on
 * both surfaces and only its presentation differs.
 */

/**
 * Order notes across the reservation. The single `order` and the `orders` collection can both carry
 * one; duplicates are collapsed so a note repeated on every order shows once.
 */
export const orderNotes = (data: ReservationsDataOutDTO): string[] => {
  const notes = [data.order?.orderNote, ...(data.orders ?? []).map(o => o.orderNote)].filter(note => isNotBlank(note)) as string[];
  return Array.from(new Set(notes));
};

/** Null rather than 0 when no order reports a weight, so the fragment can hide instead of showing "0,00 kg". */
export const totalWeight = (data: ReservationsDataOutDTO): number | null => {
  const values = (data.orders ?? []).map(o => o.orderTotalWeight).filter(v => v != null) as number[];
  return values.length ? values.reduce((sum, v) => sum + v, 0) : null;
};

export const totalVolume = (data: ReservationsDataOutDTO): number | null => {
  const values = (data.orders ?? []).map(o => o.totalVolume).filter(v => v != null) as number[];
  return values.length ? values.reduce((sum, v) => sum + v, 0) : null;
};

export const formattedWeight = (data: ReservationsDataOutDTO): string | null => {
  const value = totalWeight(data);
  return value == null ? null : formatWeight(value);
};

export const formattedVolume = (data: ReservationsDataOutDTO): string | null => {
  const value = totalVolume(data);
  return value == null ? null : formatVolume(value);
};

export const driverLines = (data: ReservationsDataOutDTO): string[] =>
  [data.driverInfo?.driverName, data.driverInfo?.driverPhone].filter(line => isNotBlank(line)) as string[];

export const supplierNotificationLabel = (data: ReservationsDataOutDTO): string | null => {
  const sn = data.linkedSupplierNotification;
  if (!sn) return null;
  const parts = [sn.supplierCompanyName, sn.applicationId].filter(part => isNotBlank(part));
  return parts.length ? parts.join(' – ') : null;
};
